import { Component, Inject, OnInit } from '@angular/core';
import { PricingExpertOpinionStepComponent } from '@app/ps/titles/components/pricing-expert-opinion-step/pricing-expert-opinion-step.component';
import { AuthService } from '@app/common/services/auth.service';
import { OpinionModel } from '@app/ps/models/opinion.model';
import { TitleModel } from '@app/common/models/title.model';
import { ExpertModel } from '@app/ps/models/expert.model';
import { UploadFileExtended } from '@app/common/components/fileupload/fileupload.component';
import { ListModel } from '@app/common/models/list.model';
import { AttachmentModel } from '@app/common/models/attachmentModel';
import { ListService } from '@app/common/services/list.service';
import { AttachmentTypeModel } from '@app/common/models/attachment-type.model';
import { Restangular } from 'ngx-restangular';
import { TitleNamePipe } from '@app/common/pipes/title-name.pipe';
import { AreaModel } from '@app/common/models/area.model';
import { DocumentModel } from '@app/common/models/document.model';
import { DisabledAttachmentType } from '@app/common/components/attachment-choose-type-form/attachment-choose-type-form.component';
import { APP_BRAND } from '@app/common/services/config.service';
import { DialogService } from '@app/common/services/dialog.service';
import { PricingExpertOpinionChangeDocumentDialogComponent } from '@app/ps/titles/components/pricing-expert-opinion-change-document-dialog/pricing-expert-opinion-change-document-dialog.component';
import { PricingExpertOpinionComponent } from '@app/ps/titles/components/pricing-expert-opinion/pricing-expert-opinion.component';

@Component({
  selector: 'pricing-expert-opinion-base',
  templateUrl: './pricing-expert-opinion-base.component.html',
})
export class PricingExpertOpinionBaseComponent extends PricingExpertOpinionStepComponent implements OnInit {

  static readonly MSG_FILES_DEFAULT = 'Vložte soubor přetažením nebo výběrem. Je možné vložit jeden soubor PDF.';
  static readonly MSG_FILES_EXTENSION = 'Soubor musí být ve formátu PDF.';
  static readonly MSG_FILES_QUANTITY  = 'Je možné vložit pouze jeden soubor.';

  multipleOpinionsPerParcelEnabled = this.authService.getActualProject().multipleOpinionsPerParcel;
  filesMsg = PricingExpertOpinionBaseComponent.MSG_FILES_DEFAULT;
  filesValid = false;
  attachmentList: ListModel<AttachmentModel>;
  attachmentType = AttachmentTypeModel.createById('OPINION_SCAN');
  documentList: ListModel<DocumentModel>;
  attachmentTypes: AttachmentTypeModel[] = [];
  disabledAttachmentTypes: DisabledAttachmentType[] = [];

  selectTitle = false;
  area: AreaModel;
  title: TitleModel;

  titleFilter = {
    validity: { values: ['valid'] },
    areaId: { values: [] },
  };

  constructor(
    private restangular: Restangular,
    private listService: ListService,
    protected authService: AuthService,
    public titleNamePipe: TitleNamePipe,
    private dialogService: DialogService,
    @Inject(APP_BRAND) public APP_BRAND: any,
  ) {
    super(authService);
    this.onSubmit = this.onSubmit.bind(this);
  }

  async ngOnInit() {
    if (!this.data.opinion) {
      this.data.opinion = this.data.occupationType === 'P'
        ? OpinionModel.createDefaultExpertOpinionPermanent()
        : OpinionModel.createDefaultExpertOpinionTemporary();

      this.data.opinion.title = this.data.title ? { id: this.data.title.id } as TitleModel : undefined;
      this.data.opinion.parcelPrices = [];

      if (this.data.opinionRequest) {
        this.data.opinion.expertOpinionNumber = this.data.opinionRequest.name;
        this.data.opinion.expert = this.data.opinionRequest.expert;
        this.data.opinion.expertOpinionDate = this.data.opinionRequest.realizedDate;
      }
    }

    this.selectTitle = !this.data.title;

    await this.loadAttachments();

    if (this.APP_BRAND.NAME === 'RSD') {
      await this.loadDocuments();
    }

    this.loading = false;
  }

  onChangeExpert(expert: ExpertModel) {
    this.data.opinion.expert = expert;
    if (this.data.opinionRequest) {
      this.data.opinionRequest.expert = expert;
    }
  }

  isFormValid(): boolean {
    const hasTitle = this.data.title;
    const hasFiles = this.filesValid || (this.data.opinionRequest && this.data.opinionRequest.id);
    const hasNumber = this.data.opinion.expertOpinionNumber;
    const hasDate = this.data.opinion.expertOpinionDate;
    const hasExpert = this.data.opinion.expert;
    const hasContent = this.data.occupationType === 'T' || (
      this.data.opinion.expertOpinionBuildings
      || this.data.opinion.expertOpinionLand
      || this.data.opinion.expertOpinionVegetation
    );
    const hasOpinionRequestContent = !this.data.opinionRequest || !this.data.opinionRequest.lastStatusChangeDate || (
      this.data.opinionRequest.realizationStartedDate
      && this.data.opinionRequest.realizationFinishExpectedDate
      && this.data.opinionRequest.realizedDate
    );

    return !!(hasFiles && hasTitle && hasNumber && hasDate && hasExpert && hasContent && hasOpinionRequestContent) || this.data.readonly;
  }

  shouldChangeDocument(): boolean {
    const fingerprint = PricingExpertOpinionComponent.getOpinionFingerprint(this.data.opinion);
    return this.APP_BRAND.NAME === 'RSD'
      && this.data.opinion
      && this.data.opinion.id
      && fingerprint !== this.data.fingerprint;
  }

  onSubmit() {
    if (this.shouldChangeDocument()) {
      const dialog = this.dialogService.open(PricingExpertOpinionChangeDocumentDialogComponent, {
        data: { opinionRequest: this.data.opinionRequest, documentList: this.documentList }
      });

      const sub = dialog.afterClosed.subscribe(async (res: boolean) => {
        if (res) {
          await this.loadDocuments();
        }
        this.data.fingerprint = PricingExpertOpinionComponent.getOpinionFingerprint(this.data.opinion);
        sub.unsubscribe();
        this.submitCallback.emit();
      });
    } else {
      this.submitCallback.emit();
    }
  }

  onFileChange(files: UploadFileExtended[]) {
    this.data.files = files;
    const extensionValid = files.every(f => f.extension && f.extension.toLowerCase() === 'pdf');

    if (this.data.files.length === 0) {
      this.filesMsg = PricingExpertOpinionBaseComponent.MSG_FILES_DEFAULT;
    } else if (!extensionValid) {
      this.filesMsg = PricingExpertOpinionBaseComponent.MSG_FILES_EXTENSION;
    } else if (this.data.files.length > 1) {
      this.filesMsg = PricingExpertOpinionBaseComponent.MSG_FILES_QUANTITY;
    } else {
      this.filesMsg = '';
    }

    this.filesValid = (extensionValid && this.data.files.length === 1);
  }

  onCadastreChanged() {
    this.titleFilter.areaId.values = [this.area.id];
    this.data.title = undefined;
    this.data.opinion.title = undefined;
  }

  onTitleChanged() {
    this.data.title = this.title;
    this.data.opinion.title = this.title;
  }

  private loadAttachments(): Promise<any> {
    if (!this.data.opinionRequest) {
      return Promise.resolve();
    }

    this.attachmentList = this.listService.createList(
      'attachments',
      {
        filters: { opinionRequestId: this.data.opinionRequest.id, cancelled: false },
        sortOrder: { sortBy: 'timestamp', direction: 'desc' }
      }
    );
    return this.listService.fetchResult(this.attachmentList);
  }

  public loadDocuments(): Promise<any> {
    if (!this.data.opinionRequest || this.APP_BRAND.NAME !== 'RSD') {
      return Promise.resolve();
    }

    this.documentList = this.listService.createList(
      'documents',
      {
        limit: null,
        filters: {
          opinionRequestId: this.data.opinionRequest.id,
          cancelled: false,
          classified: true,
          loadCollections: ['components', 'folder.relations']
        }
      }
    );
    return this.listService.fetchResult(this.documentList);
  }

  canEdit(): boolean {
    return !this.data.readonly && this.authService.hasPermission('manage_opinions');
  }
}
